import React, { useState, createContext, useContext, useEffect } from 'react';
import { IndianRupee, Landmark, Upload, FileCheck, CircleUser, Phone, Mail, FileUp, Building, User, Briefcase, FileText, CheckCircle, Clock, TrendingUp, ShieldCheck, ClipboardList } from 'lucide-react';
import { PieChart, Pie, Cell, Tooltip, Legend } from 'recharts';

// Shadcn UI Components (simulated for this environment)
const Button = ({ children, variant = 'default', className = '', ...props }) => {
    // Added default padding (py-2 px-4) to base classes for all buttons
    // This increases the button size and adds space around the text.
    const baseClasses = "inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 py-3 px-6";
    const variants = {
        default: "bg-blue-600 text-white hover:bg-blue-700",
        destructive: "bg-red-500 text-white hover:bg-red-600",
        outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary: "bg-gray-200 text-gray-800 hover:bg-gray-300",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
    };
    return <button className={`${baseClasses} ${variants[variant]} ${className}`} {...props}>{children}</button>;
};

const Card = ({ children, className = '' }) => <div className={`rounded-xl border bg-white text-card-foreground shadow-sm ${className}`}>{children}</div>;
const CardHeader = ({ children, className = '' }) => <div className={`flex flex-col space-y-1.5 p-6 ${className}`}>{children}</div>;
const CardTitle = ({ children, className = '' }) => <h3 className={`text-2xl font-semibold leading-none tracking-tight ${className}`}>{children}</h3>;
const CardDescription = ({ children, className = '' }) => <p className={`text-sm text-muted-foreground ${className}`}>{children}</p>;
const CardContent = ({ children, className = '' }) => <div className={`p-6 pt-0 ${className}`}>{children}</div>;
const CardFooter = ({ children, className = '' }) => <div className={`flex items-center p-6 pt-0 ${className}`}>{children}</div>;

const Input = ({ className = '', ...props }) => <input className={`flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`} {...props} />;
const Label = ({ children, ...props }) => <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70" {...props}>{children}</label>;
const Select = ({ children, className = '', ...props }) => <select className={`flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`} {...props}>{children}</select>;


// App Context for state management
const AppContext = createContext();

const AppProvider = ({ children }) => {
    const [page, setPage] = useState('home');
    const [applicationData, setApplicationData] = useState({
        personal: {},
        employment: {},
        loan: {},
        documents: {},
        status: 'Not Submitted',
        applicationId: null,
    });

    const updateApplicationData = (section, data) => {
        setApplicationData(prev => ({ ...prev, [section]: { ...prev[section], ...data } }));
    };
    
    const submitApplication = () => {
        const newApplicationId = `LOAN${Date.now()}`;
        setApplicationData(prev => ({...prev, status: 'Submitted', applicationId: newApplicationId}));
        setPage('track');
    };

    return (
        <AppContext.Provider value={{ page, setPage, applicationData, updateApplicationData, submitApplication }}>
            {children}
        </AppContext.Provider>
    );
};

// Main App Component
export default function App() {
    return (
        <AppProvider>
            <div className="bg-gray-50 min-h-screen font-sans">
                <Header />
                <main className="container mx-auto px-4 py-8">
                    <PageContent />
                </main>
                <Footer />
            </div>
        </AppProvider>
    );
}

// Header Component
const Header = () => {
    const { setPage } = useContext(AppContext);
    
    const navLinks = [
        { id: 'home', label: 'Home' },
        { id: 'about', label: 'About Us'},
        { id: 'eligibility', label: 'Eligibility Check' },
        { id: 'apply', label: 'Apply for Loan' },
        { id: 'upload', label: 'Upload Documents' },
        { id: 'track', label: 'Track Application' },
        { id: 'contact', label: 'Contact Us' },
    ];

    return (
        <header className="bg-white shadow-md">
            <nav className="container mx-auto px-4 py-4 flex justify-between items-center">
                {/* Logo Section */}
                <div className="flex items-center space-x-3 cursor-pointer" onClick={() => setPage('home')}>
                   <div className="text-2xl font-bold border border-gray-300 rounded-md px-3 py-1">
                       <span className="text-orange-500">Wise</span>
                       <span className="text-slate-800">Cuckoo</span>
                   </div>
                </div>

                {/* Navigation Section */}
                <div className="pl-8">
                    {/* Desktop Navigation */}
                    <div className="hidden md:flex items-center space-x-8">
                        {navLinks.map(link => (
                             <button key={link.id} onClick={() => setPage(link.id)} className="text-gray-600 hover:text-blue-600 transition-colors font-medium">
                               {link.label}
                             </button>
                        ))}
                    </div>

                    {/* Mobile Navigation */}
                    <div className="md:hidden">
                        <select onChange={(e) => setPage(e.target.value)} className="bg-gray-100 border-2 border-gray-200 rounded-md px-3 py-2">
                             {navLinks.map(link => (
                                 <option key={link.id} value={link.id}>{link.label}</option>
                             ))}
                        </select>
                    </div>
                </div>
            </nav>
        </header>
    );
};


// Page Content Router
const PageContent = () => {
    const { page } = useContext(AppContext);

    switch (page) {
        case 'home':
            return <HomePage />;
        case 'about':
            return <AboutUsPage />;
        case 'eligibility':
            return <EligibilityPage />;
        case 'apply':
            return <ApplicationPage />;
        case 'upload':
            return <DocumentUploadPage />;
        case 'track':
            return <TrackApplicationPage />;
        case 'contact':
            return <ContactPage />;
        default:
            return <HomePage />;
    }
};

// Reusable Contact Form Component
const ContactForm = () => {
    return (
        <form className="space-y-4">
            <div>
                <Label htmlFor="contact-name">Your Name</Label>
                <Input id="contact-name" placeholder="Client's Name" />
            </div>
             <div>
                <Label htmlFor="contact-email">Your Email</Label>
                <Input id="contact-email" type="email" placeholder="client@email.com" />
            </div>
             <div>
                <Label htmlFor="contact-message">Message</Label>
                <textarea id="contact-message" rows="4" className="w-full p-2 border rounded-md" placeholder="Your query..."></textarea>
            </div>
            <Button className="w-full">Send Message</Button>
        </form>
    );
}

// Home Page
const HomePage = () => {
    const { setPage } = useContext(AppContext);

    return (
        <div className="space-y-16">
            <Card className="max-w-4xl mx-auto overflow-hidden">
                <div className="grid md:grid-cols-2 items-center">
                    <div className="p-8 md:p-12 text-left">
                        <h1 className="text-4xl md:text-5xl font-extrabold text-gray-800 mb-4">Your Trusted Partner in Securing Loans</h1>
                        <p className="text-lg text-gray-600 mb-8">
                            Navigating the loan process can be complex. We simplify it for you, providing end-to-end assistance to help you secure the financing you need from leading Indian banks.
                        </p>
                        <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
                            <Button onClick={() => setPage('eligibility')} className="text-lg w-full sm:w-auto">Check Eligibility Now</Button>
                            <Button onClick={() => setPage('apply')} variant="outline" className="text-lg border-blue-600 text-blue-600 w-full sm:w-auto">Start Application</Button>
                        </div>
                    </div>
                    <div className="hidden md:flex items-center justify-center bg-blue-50 h-full">
                         <div className="text-center p-12">
                            <h2 className="text-4xl font-bold">
                                <span className="text-orange-500">Wise</span>
                                <span className="text-slate-800">Cuckoo</span>
                            </h2>
                            <p className="text-lg text-slate-600 mt-2">Reach your dream home faster & effortless</p>
                        </div>
                    </div>
                </div>
            </Card>
            <div className="text-center">
                 <h2 className="text-3xl font-bold text-gray-800 mb-8">How It Works</h2>
                 <div className="grid md:grid-cols-4 gap-8 max-w-6xl mx-auto">
                     <div className="bg-white p-6 rounded-lg shadow">
                         <FileCheck className="h-12 w-12 mx-auto text-blue-600 mb-4" />
                         <h3 className="text-xl font-semibold mb-2">1. Check Eligibility</h3>
                         <p className="text-gray-600">Answer a few simple questions to get a preliminary eligibility report.</p>
                     </div>
                     <div className="bg-white p-6 rounded-lg shadow">
                         <FileText className="h-12 w-12 mx-auto text-blue-600 mb-4" />
                         <h3 className="text-xl font-semibold mb-2">2. Apply & Upload</h3>
                         <p className="text-gray-600">Fill out our comprehensive online form and securely upload documents.</p>
                     </div>
                     <div className="bg-white p-6 rounded-lg shadow">
                         <TrendingUp className="h-12 w-12 mx-auto text-blue-600 mb-4" />
                         <h3 className="text-xl font-semibold mb-2">3. Get Best Options</h3>
                         <p className="text-gray-600">We recommend loan options that best fit your profile with a high likelihood of approval.</p>
                     </div>
                     <div className="bg-white p-6 rounded-lg shadow">
                         <Clock className="h-12 w-12 mx-auto text-blue-600 mb-4" />
                         <h3 className="text-xl font-semibold mb-2">4. Track & Get Approved</h3>
                         <p className="text-gray-600">Track your application status in real-time and get notified upon approval.</p>
                     </div>
                 </div>
            </div>

            {/* Contact Form Section on Home Page */}
            <div className="max-w-4xl mx-auto">
                <Card>
                    <CardHeader className="text-center">
                        <CardTitle>Get In Touch</CardTitle>
                        <CardDescription>Have questions? We're here to help.</CardDescription>
                    </CardHeader>
                    <CardContent>
                       <ContactForm />
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

// About Us Page
const AboutUsPage = () => {
    return (
        <Card className="max-w-4xl mx-auto">
            <CardHeader className="text-center">
                <CardTitle className="text-4xl font-extrabold text-gray-800">From Financial Wisdom to Your Dream Home</CardTitle>
                <CardDescription className="text-xl text-gray-500 mt-2">Our Journey, Your Advantage.</CardDescription>
            </CardHeader>
            <CardContent className="prose lg:prose-xl max-w-none text-gray-700 space-y-8 p-8">
                <p className="text-lg">
                    For decades, the minds behind Wise Cuckoo have been trusted pillars in the complex worlds of finance and strategy. With a rich, collective history spanning nearly 40 years in Tax Advisory, meticulous Financial Planning, and disciplined Project Management, our team has guided countless individuals and businesses toward financial stability and success.
                </p>
                <p className="text-lg">
                    Today, we channel this wealth of experience into a singular, passionate mission: <strong className="font-semibold">helping you achieve the dream of homeownership.</strong> We believe that owning a home is one of life's most profound milestones, and the path to it should be clear, confident, and navigated with an expert partner at your side.
                </p>

                <div className="bg-gray-100 rounded-xl p-8">
                    <h3 className="text-2xl font-bold text-center mb-6">Our Core Expertise</h3>
                    <div className="grid md:grid-cols-3 gap-8 text-center">
                        <div className="flex flex-col items-center">
                            <ShieldCheck className="h-12 w-12 text-blue-600 mb-3" />
                            <h4 className="font-semibold text-lg">Tax Advisory</h4>
                            <p className="text-sm">Ensuring your home loan is structured for long-term financial health and efficiency.</p>
                        </div>
                        <div className="flex flex-col items-center">
                            <TrendingUp className="h-12 w-12 text-blue-600 mb-3" />
                            <h4 className="font-semibold text-lg">Financial Planning</h4>
                            <p className="text-sm">Integrating your home purchase seamlessly into your broader life and financial goals.</p>
                        </div>
                        <div className="flex flex-col items-center">
                            <ClipboardList className="h-12 w-12 text-blue-600 mb-3" />
                            <h4 className="font-semibold text-lg">Project Management</h4>
                            <p className="text-sm">Managing the entire loan process with precision, from application to final approval.</p>
                        </div>
                    </div>
                </div>

                <blockquote className="border-l-4 border-orange-500 pl-4 italic text-gray-600">
                    "Our focus shifted when we saw a critical need. Aspiring homeowners weren't just looking for loans; they were looking for clarity, confidence, and a guide they could trust. That's the role we are proud to fill."
                </blockquote>

                <p className="text-lg">
                    At Wise Cuckoo, we don't just process paperwork. We build relationships. We leverage our deep financial acumen to analyze your unique situation, identify the best possible loan products across multiple banks, and chart a course that maximizes your chances of approval. Let our experience be the foundation upon which you build your dream home.
                </p>
            </CardContent>
        </Card>
    );
};


// Eligibility Check Page
const EligibilityPage = () => {
    const [income, setIncome] = useState('');
    const [loanAmount, setLoanAmount] = useState('');
    const [tenure, setTenure] = useState('5');
    const [result, setResult] = useState(null);
    const { setPage } = useContext(AppContext);

    const calculateEligibility = (e) => {
        e.preventDefault();
        const monthlyIncome = parseFloat(income);
        const requestedLoan = parseFloat(loanAmount);
        
        if (isNaN(monthlyIncome) || isNaN(requestedLoan) || monthlyIncome <= 0 || requestedLoan <= 0) {
            setResult({ eligible: false, message: 'Please enter valid positive numbers for income and loan amount.' });
            return;
        }

        const maxEmi = monthlyIncome * 0.5; // Assuming max 50% of income can be EMI
        const interestRate = 0.12; // Assuming 12% annual interest rate
        const monthlyRate = interestRate / 12;
        const loanTenureMonths = parseInt(tenure) * 12;
        
        const calculatedEmi = (requestedLoan * monthlyRate * Math.pow(1 + monthlyRate, loanTenureMonths)) / (Math.pow(1 + monthlyRate, loanTenureMonths) - 1);

        if (calculatedEmi <= maxEmi) {
            setResult({
                eligible: true,
                message: `Congratulations! You are likely eligible for a loan of ₹${requestedLoan.toLocaleString('en-IN')}.`,
                emi: calculatedEmi.toFixed(2),
                maxLoan: ((maxEmi * (Math.pow(1 + monthlyRate, loanTenureMonths) - 1)) / (monthlyRate * Math.pow(1 + monthlyRate, loanTenureMonths))).toFixed(2),
            });
        } else {
            setResult({
                eligible: false,
                message: `Based on your income, the requested loan amount might be too high.`,
                emi: calculatedEmi.toFixed(2),
                maxLoan: ((maxEmi * (Math.pow(1 + monthlyRate, loanTenureMonths) - 1)) / (monthlyRate * Math.pow(1 + monthlyRate, loanTenureMonths))).toFixed(2),
            });
        }
    };
    
    const pieData = result && result.eligible ? [
        { name: 'EMI', value: parseFloat(result.emi) },
        { name: 'Remaining Income', value: parseFloat(income) - parseFloat(result.emi) }
    ] : [];

    const COLORS = ['#FF8042', '#00C49F'];

    return (
        <Card className="max-w-4xl mx-auto">
            <CardHeader>
                <CardTitle>Loan Eligibility Calculator</CardTitle>
                <CardDescription>Get a quick estimate of your loan eligibility.</CardDescription>
            </CardHeader>
            <CardContent>
                <form onSubmit={calculateEligibility} className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                        <div>
                            <Label htmlFor="income">Your Monthly Income (₹)</Label>
                            <Input id="income" type="number" placeholder="e.g., 50000" value={income} onChange={(e) => setIncome(e.target.value)} required />
                        </div>
                        <div>
                            <Label htmlFor="loanAmount">Desired Loan Amount (₹)</Label>
                            <Input id="loanAmount" type="number" placeholder="e.g., 500000" value={loanAmount} onChange={(e) => setLoanAmount(e.target.value)} required />
                        </div>
                        <div>
                            <Label htmlFor="tenure">Loan Tenure (Years)</Label>
                            <Select id="tenure" value={tenure} onChange={(e) => setTenure(e.target.value)}>
                                <option value="1">1 Year</option>
                                <option value="2">2 Years</option>
                                <option value="3">3 Years</option>
                                <option value="4">4 Years</option>
                                <option value="5">5 Years</option>
                                <option value="7">7 Years</option>
                                <option value="10">10 Years</option>
                                <option value="15">15 Years</option>
                            </Select>
                        </div>
                        <Button type="submit" className="w-full">Calculate</Button>
                    </div>
                    {result && (
                        <div className={`p-6 rounded-lg ${result.eligible ? 'bg-green-100' : 'bg-red-100'}`}>
                            <div className="flex flex-col items-center justify-center text-center">
                                <h3 className={`text-xl font-bold ${result.eligible ? 'text-green-800' : 'text-red-800'}`}>{result.message}</h3>
                                {result.eligible && <p className="text-gray-700 mt-2">Estimated EMI: <span className="font-semibold">₹{result.emi}</span></p> }
                                <p className="text-gray-700">Based on our calculation, you may be eligible for a loan up to <span className="font-semibold">₹{parseFloat(result.maxLoan).toLocaleString('en-IN')}</span>.</p>
                                
                                {result.eligible ? (
                                    <>
                                        {pieData.length > 0 && (
                                           <div className="w-full h-48 mt-4">
                                              <PieChart width={300} height={200}>
                                                <Pie data={pieData} cx="50%" cy="50%" outerRadius={60} fill="#8884d8" dataKey="value" label>
                                                   {pieData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                                                </Pie>
                                                <Tooltip formatter={(value) => `₹${Number(value).toLocaleString('en-IN')}`} />
                                                <Legend />
                                              </PieChart>
                                           </div>
                                        )}
                                        <Button onClick={() => setPage('apply')} className="mt-4">Apply Now</Button>
                                    </>
                                ) : (
                                    <div className="mt-6 w-full max-w-md text-left bg-white p-6 rounded-lg shadow-inner">
                                        <h4 className="font-semibold text-center text-gray-800 mb-2">Talk to an Expert</h4>
                                        <p className="text-sm text-center text-gray-600 mb-4">For a detailed discussion and personalized solutions, please leave your details below.</p>
                                        <ContactForm />
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </form>
            </CardContent>
        </Card>
    );
};

// Application Form Page
const ApplicationPage = () => {
    const { updateApplicationData, setPage, applicationData } = useContext(AppContext);
    const [formData, setFormData] = useState({
      personal: applicationData.personal || {},
      employment: applicationData.employment || {},
      loan: applicationData.loan || {}
    });

    const handleChange = (section, e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [section]: { ...prev[section], [name]: value }
        }));
    };
    
    const handleSubmit = (e) => {
        e.preventDefault();
        updateApplicationData('personal', formData.personal);
        updateApplicationData('employment', formData.employment);
        updateApplicationData('loan', formData.loan);
        setPage('upload');
    };

    return (
        <Card className="max-w-4xl mx-auto">
            <CardHeader>
                <CardTitle>Loan Application Form</CardTitle>
                <CardDescription>Please fill in your details accurately.</CardDescription>
            </CardHeader>
            <CardContent>
                <form onSubmit={handleSubmit} className="space-y-8">
                    {/* Personal Details */}
                    <div className="space-y-4">
                        <h4 className="text-lg font-semibold border-b pb-2 flex items-center"><User className="mr-2 h-5 w-5 text-blue-600"/>Personal Details</h4>
                        <div className="grid md:grid-cols-2 gap-4">
                            <div><Label htmlFor="fullName">Full Name</Label><Input id="fullName" name="fullName" value={formData.personal.fullName || ''} onChange={(e) => handleChange('personal', e)} required/></div>
                            <div><Label htmlFor="dob">Date of Birth</Label><Input id="dob" name="dob" type="date" value={formData.personal.dob || ''} onChange={(e) => handleChange('personal', e)} required/></div>
                            <div><Label htmlFor="pan">PAN Card Number</Label><Input id="pan" name="pan" value={formData.personal.pan || ''} onChange={(e) => handleChange('personal', e)} required/></div>
                            <div><Label htmlFor="aadhaar">Aadhaar Number</Label><Input id="aadhaar" name="aadhaar" value={formData.personal.aadhaar || ''} onChange={(e) => handleChange('personal', e)} required/></div>
                            <div><Label htmlFor="phone">Mobile Number</Label><Input id="phone" name="phone" type="tel" value={formData.personal.phone || ''} onChange={(e) => handleChange('personal', e)} required/></div>
                            <div><Label htmlFor="email">Email Address</Label><Input id="email" name="email" type="email" value={formData.personal.email || ''} onChange={(e) => handleChange('personal', e)} required/></div>
                            <div className="md:col-span-2"><Label htmlFor="address">Current Address</Label><Input id="address" name="address" value={formData.personal.address || ''} onChange={(e) => handleChange('personal', e)} required/></div>
                        </div>
                    </div>

                    {/* Employment Details */}
                    <div className="space-y-4">
                         <h4 className="text-lg font-semibold border-b pb-2 flex items-center"><Briefcase className="mr-2 h-5 w-5 text-blue-600"/>Employment Details</h4>
                         <div className="grid md:grid-cols-2 gap-4">
                            <div><Label htmlFor="empType">Employment Type</Label>
                                <Select id="empType" name="empType" value={formData.employment.empType || ''} onChange={(e) => handleChange('employment', e)} required>
                                    <option>Select...</option>
                                    <option value="Salaried">Salaried</option>
                                    <option value="Self-Employed">Self-Employed</option>
                                </Select>
                            </div>
                             <div><Label htmlFor="companyName">Company Name</Label><Input id="companyName" name="companyName" value={formData.employment.companyName || ''} onChange={(e) => handleChange('employment', e)} /></div>
                             <div><Label htmlFor="monthlyIncome">Net Monthly Income (₹)</Label><Input id="monthlyIncome" name="monthlyIncome" type="number" value={formData.employment.monthlyIncome || ''} onChange={(e) => handleChange('employment', e)} required /></div>
                         </div>
                    </div>
                    
                    {/* Loan Details */}
                    <div className="space-y-4">
                         <h4 className="text-lg font-semibold border-b pb-2 flex items-center"><IndianRupee className="mr-2 h-5 w-5 text-blue-600"/>Loan Details</h4>
                         <div className="grid md:grid-cols-2 gap-4">
                             <div><Label htmlFor="loanAmount">Loan Amount Required (₹)</Label><Input id="loanAmount" name="loanAmount" type="number" value={formData.loan.loanAmount || ''} onChange={(e) => handleChange('loan', e)} required /></div>
                             <div><Label htmlFor="loanTenure">Loan Tenure (Months)</Label><Input id="loanTenure" name="loanTenure" type="number" value={formData.loan.loanTenure || ''} onChange={(e) => handleChange('loan', e)} required /></div>
                         </div>
                    </div>

                    <CardFooter>
                        <Button type="submit" className="w-full md:w-auto ml-auto">Save and Proceed to Document Upload</Button>
                    </CardFooter>
                </form>
            </CardContent>
        </Card>
    );
};

// Document Upload Page
const DocumentUploadPage = () => {
    const { updateApplicationData, submitApplication, applicationData } = useContext(AppContext);
    const [files, setFiles] = useState(applicationData.documents || {});

    const handleFileChange = (docType, e) => {
        const file = e.target.files[0];
        if (file) {
            setFiles(prev => ({ ...prev, [docType]: file }));
        }
    };
    
    useEffect(() => {
        updateApplicationData('documents', files);
    }, [files]);
    
    const requiredDocs = [
        { id: 'pan_card', label: 'PAN Card' },
        { id: 'aadhaar_card', label: 'Aadhaar Card' },
        { id: 'salary_slip', label: 'Latest 3 Months Salary Slips' },
        { id: 'bank_statement', label: 'Last 6 Months Bank Statement' },
    ];

    const allDocsUploaded = requiredDocs.every(doc => files[doc.id]);

    return (
        <Card className="max-w-2xl mx-auto">
            <CardHeader>
                <CardTitle>Document Upload</CardTitle>
                <CardDescription>Please upload clear copies of the following documents.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                {requiredDocs.map(doc => (
                    <div key={doc.id} className="p-4 border rounded-lg flex items-center justify-between">
                        <div>
                           <p className="font-medium">{doc.label}</p>
                           {files[doc.id] ? 
                            <div className="text-sm text-green-600 flex items-center mt-1">
                                <CheckCircle className="h-4 w-4 mr-1"/> {files[doc.id].name}
                            </div> 
                            : <p className="text-sm text-gray-500">No file chosen</p>
                           }
                        </div>
                        <Label htmlFor={doc.id} className="cursor-pointer bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold py-2 px-4 rounded-lg inline-flex items-center">
                            <FileUp className="h-4 w-4 mr-2"/> Choose File
                        </Label>
                        <input type="file" id={doc.id} className="hidden" onChange={(e) => handleFileChange(doc.id, e)} />
                    </div>
                ))}
            </CardContent>
            <CardFooter>
                <Button onClick={submitApplication} disabled={!allDocsUploaded} className="w-full md:w-auto ml-auto" title={!allDocsUploaded ? 'Please upload all required documents' : ''}>
                    Submit Application
                </Button>
            </CardFooter>
        </Card>
    );
};

// Track Application Page
const TrackApplicationPage = () => {
    const { applicationData } = useContext(AppContext);
    const { applicationId, status } = applicationData;

    const getStatusInfo = (currentStatus) => {
        const statuses = [
            { id: 'Submitted', label: 'Application Submitted', desc: 'We have received your application.' },
            { id: 'In-Review', label: 'Under Review', desc: 'Your documents and details are being verified.' },
            { id: 'Approved', label: 'Loan Approved', desc: 'Congratulations! Your loan has been approved.' },
            { id: 'Rejected', label: 'Application Rejected', desc: 'We regret to inform you that your application was not approved.' },
        ];
        
        let currentStatusIndex = statuses.findIndex(s => s.id === currentStatus);
        if (currentStatusIndex === -1 && currentStatus !== 'Not Submitted') {
             currentStatusIndex = 0; // Default to submitted if status is unknown but application exists
        }

        return statuses.map((s, index) => ({
            ...s,
            isCompleted: index < currentStatusIndex,
            isCurrent: index === currentStatusIndex,
            isFuture: index > currentStatusIndex,
        }));
    };
    
    // Simulate status update
    const [currentStatus, setCurrentStatus] = useState(status);
    useEffect(() => {
        if(status === 'Submitted') {
            setTimeout(() => setCurrentStatus('In-Review'), 3000);
        }
    }, [status])

    const statusTimeline = getStatusInfo(currentStatus);


    if (!applicationId) {
        return (
            <Card className="max-w-2xl mx-auto text-center">
                <CardHeader>
                    <CardTitle>Track Your Application</CardTitle>
                </CardHeader>
                <CardContent>
                    <p className="text-gray-600">You have not submitted an application yet. Please complete the application form to track its status.</p>
                </CardContent>
            </Card>
        );
    }
    
    return (
        <Card className="max-w-2xl mx-auto">
            <CardHeader className="text-center">
                <CardTitle>Application Status</CardTitle>
                <CardDescription>Application ID: {applicationId}</CardDescription>
            </CardHeader>
            <CardContent>
                <div className="space-y-8">
                    {statusTimeline.map((s, index) => (
                         <div key={s.id} className="flex items-start">
                            <div className="flex flex-col items-center mr-4">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center
                                    ${s.isCurrent ? 'bg-blue-600 text-white animate-pulse' : ''}
                                    ${s.isCompleted ? 'bg-green-500 text-white' : ''}
                                    ${s.isFuture ? 'bg-gray-300' : ''}
                                `}>
                                    {s.isCompleted ? <CheckCircle size={20}/> : <span>{index + 1}</span>}
                                </div>
                                {index < statusTimeline.length - 1 && <div className={`w-0.5 grow mt-2 ${s.isCompleted ? 'bg-green-500' : 'bg-gray-300'}`}></div>}
                            </div>
                            <div>
                                <h4 className={`font-semibold ${s.isCurrent ? 'text-blue-600' : ''}`}>{s.label}</h4>
                                <p className="text-sm text-gray-500">{s.desc}</p>
                            </div>
                         </div>
                    ))}
                </div>
            </CardContent>
        </Card>
    );
};

// Contact Us Page
const ContactPage = () => {
    return (
        <Card className="max-w-4xl mx-auto">
            <CardHeader className="text-center">
                <CardTitle>Get In Touch</CardTitle>
                <CardDescription>We are here to help you. Reach out to us for any queries.</CardDescription>
            </CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-8">
                <div className="space-y-6">
                     <div className="flex items-center">
                        <Phone className="h-6 w-6 mr-3 text-blue-600" />
                        <p className="text-gray-600">+91 XXXX-XXX-XXX</p>
                    </div>
                     <div className="flex items-center">
                        <Mail className="h-6 w-6 mr-3 text-blue-600" />
                        <p className="text-gray-600">wisecuckoo@gmail.com</p>
                    </div>
                    <div>
                        <h4 className="font-semibold text-gray-700 mb-2">Our Offices</h4>
                        <div className="space-y-2 pl-2">
                            <div className="flex items-center">
                                <Building className="h-5 w-5 mr-3 text-blue-600 flex-shrink-0" />
                                <p className="text-gray-600">Teynampet, Chennai</p>
                            </div>
                            <div className="flex items-center">
                                <Building className="h-5 w-5 mr-3 text-blue-600 flex-shrink-0" />
                                <p className="text-gray-600">Porur, Chennai</p>
                            </div>
                        </div>
                    </div>
                </div>
                <ContactForm />
            </CardContent>
        </Card>
    );
};


// Footer Component
const Footer = () => (
    <footer className="bg-white mt-16 py-6 border-t">
        <div className="container mx-auto px-4 text-center text-gray-600">
            <p>&copy; {new Date().getFullYear()} Wise Cuckoo. All Rights Reserved.</p>
            <p className="text-sm mt-1">Disclaimer: Loan approval is at the sole discretion of the bank.</p>
        </div>
    </footer>
); 